import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getTherapistAppointmentStats } from '../../redux/therapistSlice';
import { 
  FiMapPin, 
  FiClock, 
  FiStar, 
  FiDollarSign, 
  FiCalendar,
  FiChevronRight,
  FiCheckCircle,
  FiXCircle
} from 'react-icons/fi';

const AdminTherapistInfo = ({
  therapistName,
  therapistSurname,
  therapistTitle,
  therapyPerformed = 0,
  fee,
  imageSrc,
  therapistId,
  isActive = true,
  joinDate,
  rating = 0,
  totalSessions = 0,
  specializations = [],
  location = "Not specified",
  isApprovedByAdmin = null, // null: pending, true: approved, false: rejected
  approvalStatus = null
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const appointmentStats = useSelector((state) => state.therapist.appointmentStatsMap[therapistId]);

  useEffect(() => {
    if (therapistId) {
      dispatch(getTherapistAppointmentStats(therapistId));
    }
  }, [dispatch, therapistId]);

  // Helper to determine status
  const getStatus = () => {
    if (approvalStatus === 'approved') return 'approved';
    if (approvalStatus === 'rejected') return 'rejected';
    
    // Fallback to isApprovedByAdmin (handling both boolean and string "true"/"false")
    if (isApprovedByAdmin === true || isApprovedByAdmin === 'true') return 'approved';
    if (isApprovedByAdmin === false || isApprovedByAdmin === 'false') return 'rejected';
    
    return 'pending';
  };

  const currentStatus = getStatus();
  
  // displaySessions shows ALL appointments (including future/pending ones)
  const displaySessions = appointmentStats?.totalAppointments ?? (therapyPerformed || totalSessions);
  
  // earnings calculation is based strictly on COMPLETED appointments
  const completedSessions = appointmentStats?.completedAppointments ?? 0;

  // Calculate completion rate
  const completionRate = appointmentStats 
    ? (appointmentStats.totalAppointments > 0 
        ? Math.round((appointmentStats.completedAppointments / appointmentStats.totalAppointments) * 100) 
        : 0)
    : 0;

  return (
    <div
      key={therapistId}
      onClick={() => navigate(`/adminTherapistDetail/${therapistId}`)}
      className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 p-6 mb-4 cursor-pointer group border border-gray-100 hover:border-gray-200"
    >
      <div className="flex items-start justify-between">
        {/* Left Section: Profile Info */}
        <div className="flex items-start gap-4 flex-1">
          {/* Profile Image with Status */}
          <div className="relative">
            <img
              src={imageSrc}
              alt={`${therapistName} ${therapistSurname}`}
              className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-2xl border-2 border-gray-100 group-hover:border-blue-200 transition-colors"
            />
            {/* Status Indicator */}
            <div className="absolute -top-1 -right-1">
              {isActive ? (
                <FiCheckCircle className="w-5 h-5 text-green-500 bg-white rounded-full" />
              ) : (
                <FiXCircle className="w-5 h-5 text-red-500 bg-white rounded-full" />
              )}
            </div>
          </div>

          {/* Therapist Details */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                {therapistName} {therapistSurname}
              </h3>
              
              {/* Admin Approval Status Badge */}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                currentStatus === 'approved'
                  ? 'bg-green-100 text-green-700' 
                  : currentStatus === 'rejected'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {currentStatus === 'approved' 
                  ? '✓ Approved' 
                  : currentStatus === 'rejected'
                  ? '✗ Rejected'
                  : '⏳ Pending Review'
                }
              </span>

              {/* Active Status Badge (separate from admin approval) */}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                isActive 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            <p className="text-sm text-gray-600 mb-3 font-medium">{therapistTitle}</p>

            {/* Stats Row */}
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                {rating > 0 ? (
                  <>
                    <FiStar className="w-4 h-4 text-yellow-500" />
                    <span className="font-medium text-gray-700">{rating}</span>
                  </>
                ) : (
                  <span className="font-medium text-gray-700">-</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <FiCalendar className="w-4 h-4" />
                <span>{displaySessions} sessions</span>
              </div>
              <div className="flex items-center gap-1">
                <FiMapPin className="w-4 h-4" />
                <span>{location}</span>
              </div>
            </div>

            {/* Specializations */}
            {specializations.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {specializations.slice(0, 3).map((spec, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg font-medium"
                  >
                    {spec}
                  </span>
                ))}
                {specializations.length > 3 && (
                  <span className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded-lg font-medium">
                    +{specializations.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Section: Stats & Actions */}
        <div className="flex flex-col items-end gap-3">
          {/* Fee */}
          <div className="text-right">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
              <FiDollarSign className="w-3 h-3" />
              <span>Hourly Rate</span>
            </div>
            <p className="text-xl font-bold text-gray-900">
              £{fee}
            </p>
          </div>

          {/* Performance Metrics */}
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-xs text-gray-500">Sessions</p>
              <p className="text-lg font-bold text-blue-600">{displaySessions}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Rating</p>
              <p className="text-lg font-bold text-yellow-600">{rating > 0 ? rating : '-'}</p>
            </div>
          </div>

          {/* Join Date */}
          {joinDate && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <FiClock className="w-3 h-3" />
              <span>Joined {joinDate}</span>
            </div>
          )}

          {/* Action Arrow */}
          <div className="mt-2">
            <FiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
          </div>
        </div>
      </div>

      {/* Bottom Stats Bar (Optional) */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-500 mb-1">Total Earnings</p>
            <p className="text-sm font-semibold text-green-600">
              £{(completedSessions * (parseFloat(fee) || 0)).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Completion Rate</p>
            <p className="text-sm font-semibold text-blue-600">
              {completionRate}%
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Response Time</p>
            <p className="text-sm font-semibold text-purple-600">2h avg</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTherapistInfo;
